"use client"

import { Badge } from "@/components/ui/badge"
import { Waves, Wind, Thermometer, Clock } from "lucide-react"
import { cn } from "@/lib/utils"
import { usePreferences } from "@/contexts/preferences-context"
import { convertWaveHeight, convertWindSpeed, convertTemperatureString } from "@/lib/unit-conversions"

interface LiveConditionsBadgeProps {
  quality: string
  waveHeight: string
  windSpeed: string
  waterTemp: string
  lastUpdated: string
  kiteQuality?: string
  activityType?: "surfing" | "kitesurfing" | "both" | null
  className?: string
}

export function LiveConditionsBadge({
  quality,
  waveHeight,
  windSpeed,
  waterTemp,
  lastUpdated,
  kiteQuality,
  activityType,
  className,
}: LiveConditionsBadgeProps) {
  const { preferences } = usePreferences()

  const getQualityColor = (quality: string) => {
    switch (quality.toLowerCase()) {
      case "epic":
        return "bg-green-500 text-white"
      case "excellent":
      case "very good":
        return "bg-green-400 text-white"
      case "good":
        return "bg-blue-500 text-white"
      case "fair":
        return "bg-yellow-500 text-white"
      case "poor":
        return "bg-red-500 text-white"
      case "flat":
        return "bg-red-500 text-white"
      default:
        return "bg-gray-500 text-white"
    }
  }

  const convertTemperature = (tempString: string) => {
    return convertTemperatureString(tempString, preferences.temperatureUnit)
  }

  const timeAgo = (dateString: string) => {
    const now = new Date()
    const updated = new Date(dateString)
    const diffMinutes = Math.floor((now.getTime() - updated.getTime()) / (1000 * 60))

    if (diffMinutes < 1) return "Just now"
    if (diffMinutes < 60) return `${diffMinutes}m ago`
    const diffHours = Math.floor(diffMinutes / 60)
    if (diffHours < 24) return `${diffHours}h ago`
    return `${Math.floor(diffHours / 24)}d ago`
  }

  // Determine which badges to show based on activity_type
  // Default to showing both if activityType is not provided (backward compatibility)
  const showSurf = !activityType || activityType === "surfing" || activityType === "both"
  const showKite = activityType === "kitesurfing" || activityType === "both"

  return (
    <div className={cn("space-y-2", className)}>
      {/* Surf and Kite conditions badges */}
      <div className="flex items-center gap-2 flex-wrap">
        {showSurf && (
          <Badge className={cn("text-xs font-semibold", getQualityColor(quality))}>
            🏄 {quality} Surf
          </Badge>
        )}
        {showKite && kiteQuality && (
          <Badge className={cn("text-xs font-semibold", getQualityColor(kiteQuality))}>
            🪁 {kiteQuality} Kite
          </Badge>
        )}
      </div>

      <div className="flex items-center gap-3 text-xs text-slate-600 dark:text-slate-300">
        <div className="flex items-center gap-1">
          <Waves className="w-3 h-3 text-blue-500" />
          <span suppressHydrationWarning>{convertWaveHeight(waveHeight, preferences.unitSystem)}</span>
        </div>
        <div className="flex items-center gap-1">
          <Wind className="w-3 h-3 text-gray-500" />
          <span suppressHydrationWarning>{convertWindSpeed(windSpeed, preferences.unitSystem)}</span>
        </div>
        <div className="flex items-center gap-1">
          <Thermometer className="w-3 h-3 text-orange-500" />
          <span suppressHydrationWarning>{convertTemperature(waterTemp)}</span>
        </div>
      </div>

      <div className="flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400">
        <Clock className="w-3 h-3" />
        <span suppressHydrationWarning>Updated {timeAgo(lastUpdated)}</span>
      </div>
    </div>
  )
}